import React, { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { StockBadge } from "@/components/StockBadge";
import { CardOrQuote } from "@/components/CardOrQuote";

export const ProductVideoHero = ({ product }) => {
  const videoRef = useRef(null);
  const [shown, setShown] = useState(false);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const v = videoRef.current;
    if (v) {
      v.muted = true;
      const started = v.play();
      if (started && started.catch) started.catch(() => {});
    }
    setShown(false);
    setMuted(true);
    const timer = setTimeout(() => setShown(true), 1000);
    return () => clearTimeout(timer);
  }, [product.slug]);

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !muted;
    v.muted = next;
    if (!next) {
      v.volume = 1;
      const started = v.play();
      if (started && started.catch) started.catch(() => {});
    }
    setMuted(next);
  };

  const price = product.priceLabel || product.fromPrice;

  return (
    <>
    <section className="relative w-full bg-black overflow-hidden aspect-video sm:aspect-auto sm:h-[86vh] lg:h-[92vh]" data-testid="product-video-hero">
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        poster={product.video.poster || product.image}
        data-testid="product-video"
      >
        <source src={product.video.src} type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-gradient-to-t from-ei-ink/85 via-ei-ink/15 to-ei-ink/35 pointer-events-none" />

      <button
        type="button"
        onClick={toggleSound}
        aria-pressed={!muted}
        className="absolute top-4 right-4 sm:top-6 sm:right-6 inline-flex items-center gap-2 rounded-full bg-ei-ink/70 backdrop-blur-sm border border-white/25 text-ei-white px-4 py-2.5 min-h-[44px] font-display text-xs sm:text-sm font-bold hover:bg-ei-ink/90 hover:border-ei-gold/70 transition-colors"
        data-testid="product-video-sound-toggle"
      >
        {muted ? <><Volume2 className="w-4 h-4 text-ei-gold" /> Tap for sound</> : <><VolumeX className="w-4 h-4 text-ei-gold" /> Mute</>}
      </button>

      <div
        className={`absolute left-0 bottom-0 p-4 sm:p-8 lg:p-12 max-w-full sm:max-w-xl transition-all duration-700 ease-out ${shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        data-testid="product-video-overlay"
      >
        <div className="rounded-md bg-ei-ink/72 backdrop-blur-md border border-white/15 p-2.5 sm:p-7 max-w-[13rem] sm:max-w-none">
          <div className="flex flex-wrap items-center gap-1.5 sm:gap-3">
            <span className="ei-eyebrow text-ei-gold text-[9px] sm:text-xs">{product.category}</span>
            <StockBadge slug={product.slug} testId="hero-stock" />
          </div>
          <h2 className="mt-1 sm:mt-2 font-display text-sm sm:text-3xl font-bold text-ei-white leading-snug">{product.name}</h2>
          {price && (
            <div className="mt-1 sm:mt-3 font-display font-bold tnum text-lg sm:text-4xl text-ei-white" data-testid="product-video-price">
              {price}
              <span className="ml-1 sm:ml-2 text-[10px] sm:text-sm font-normal text-ei-white/75">+ GST</span>
            </div>
          )}
          <p className="mt-1 hidden sm:block text-xs text-ei-white/60">Wholesale, ex GST. Freight quoted separately.</p>
          <CardOrQuote
            slug={product.slug}
            quoteName={product.name}
            image={product.image}
            cardTestId="product-video-buy-cta"
            quoteTestId="product-video-quote-cta"
            dark
            className="mt-2 sm:mt-5 [&>div]:gap-1.5 sm:[&>div]:gap-3 [&_a]:text-[11px] [&_button]:text-[11px] [&_a]:px-2.5 [&_button]:px-2.5 [&_a]:py-2 [&_button]:py-2 [&_a]:min-h-0 [&_button]:min-h-0 [&_p]:hidden sm:[&_p]:flex sm:[&_a]:text-sm sm:[&_button]:text-sm sm:[&_a]:px-6 sm:[&_button]:px-6 sm:[&_a]:py-4 sm:[&_button]:py-4 sm:[&_a]:min-h-[44px] sm:[&_button]:min-h-[44px]"
          />
        </div>
      </div>
      </section>
    </>
  );
};
